import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import type { DateFormat } from "../../types/column-format";
import { relativeTimeDetailedPlugin } from "../../utils/dayjs/plugins";
import type { FormatOptions } from "./format.types";

dayjs.extend(utc);
dayjs.extend(relativeTimeDetailedPlugin);

export const formatDate = (options: FormatOptions): string => {
  const { format: columnFormat, specifier, style = "long" } = options;
  const isShort = style === "short";

  const dateFormat: DateFormat =
    columnFormat.kind === "DATE" ? columnFormat.dateFormat : "DATE";

  try {
    const raw = options.value;
    const date =
      raw instanceof Date
        ? dayjs.utc(raw)
        : typeof raw === "number"
          ? dayjs.utc(raw)
          : typeof raw === "bigint"
            ? dayjs.utc(Number(raw))
            : dayjs.utc(String(raw));

    if (!date.isValid()) throw new Error("Invalid date");

    if (specifier) {
      return date.format(specifier);
    }

    switch (dateFormat) {
      case "DATETIME":
        if (isShort) return date.format("MMM D, YY LT");
        return date.format("MMMM D, YYYY LT");
      case "TIME":
        if (isShort) return date.format("LT");
        return date.format("LTS");
      case "DATE":
        if (isShort) return date.format("MMM D, YY");
        return date.format("MMMM D, YYYY");
      case "WEEK":
        if (isShort)
          return `${date.startOf("week").format("MMM D")} - ${date.endOf("week").format("MMM D")}`;
        return `${date.startOf("week").format("MMMM D")} - ${date.endOf("week").format("MMMM D")}`;
      case "MONTH":
        if (isShort) return date.format("MMM YY");
        return date.format("MMMM YYYY");
      case "QUARTER":
        if (isShort) return `Q${date.quarter()} ${date.year()}`;
        return `Quarter ${date.quarter()} ${date.year()}`;
      case "YEAR":
        return date.format("YYYY");
      case "RELATIVE":
        return date.fromNowDetailed(isShort);
    }

    return String(options.value);
  } catch (_e) {
    return String(options.value);
  }
};
